/**
 * Sales Intelligence API service.
 * Maps all /api/sales/* endpoints.
 */
import { api } from '@isg/ui-kit';
import { withCompany } from '../company-switcher.js';

const PREFIX = '/sales';

class SalesAPI {
  // ── Filter & Dashboard ─────────────────────────────────────

  /** Filter options (divisions, salespeople) */
  getFilters(params = {}) {
    return api.get(`${PREFIX}/filters`, params);
  }

  /** Parts purchasing has flagged as stuck, with expected dates.
   *  Proxied by our backend with the end user's token — see the blueprint. */
  getProblemParts(params = {}) { return api.get(`${PREFIX}/problem-parts`, params); }

  // ── Show Leads (trade-show capture review) ─────────────────
  getShowLeads(params = {}) { return api.get(`${PREFIX}/leads`, params); }
  getShowLead(id) { return api.get(`${PREFIX}/leads/${encodeURIComponent(id)}`); }
  updateShowLead(id, data) { return api.put(`${PREFIX}/leads/${encodeURIComponent(id)}`, data); }
  getQxActions() { return api.get(`${PREFIX}/leads/qx-actions`); }
  /** Download captured leads as CSV (honours show/status/entity filters).
   *  Returns the raw Response (non-JSON) — the caller reads .blob() to save it. */
  exportShowLeads(params = {}) { return api.get(`${PREFIX}/leads/export`, params); }

  // ── Rep capture (mobile web capture, JWT-authed) ───────────
  capBootstrap(entity, company) { return api.get(`${PREFIX}/capture/bootstrap`, { entity, company }); }
  capLookup(q, entity) { return api.get(`${PREFIX}/capture/lookup`, { q, entity }); }
  capIntel(code, entity) { return api.get(`${PREFIX}/capture/customer/${encodeURIComponent(code)}/intel`, { entity }); }
  capQuoteLines(qn, entity) { return api.get(`${PREFIX}/capture/quote/${encodeURIComponent(qn)}/lines`, { entity }); }
  capSync(leads) { return api.post(`${PREFIX}/capture/leads/sync`, { leads }); }
  /** Attach a card/badge image to a lead. `fd` is a FormData (file, kind). */
  capMedia(leadId, fd) {
    return api.post(`${PREFIX}/capture/leads/${encodeURIComponent(leadId)}/media`, fd);
  }
  /** Quotes and orders from QX that MIGHT be this lead converting. Suggestions
   *  only — nothing is attributed until a rep picks one and saves. */
  leadMatchSuggestions(leadId) {
    return api.get(`${PREFIX}/leads/${encodeURIComponent(leadId)}/match-suggestions`);
  }
  /** Marketing's document catalogue for this show — the pack attached to the
   *  show's visitor form, the curated folders, and the whole library to
   *  search. Proxied server-side; reps hold no marketing grant. */
  capDocuments(showId) { return api.get(`${PREFIX}/capture/documents`, { show_id: showId || '' }); }
  /** Email a document pack to a customer. Marketing records it against them
   *  and tracks every download, so the office sees what was actually opened. */
  capSendDocuments(body) { return api.post(`${PREFIX}/capture/send-documents`, body); }
  /** Preview image URL for a document in the show's pack. Proxied through this
   *  app, so an <img src> carries the rep's JWT cookie and needs no marketing
   *  session; a type with no renderer 404s and the card keeps its badge. */
  capDocThumbUrl(docId, showId) {
    return `${PREFIX}/capture/documents/${docId}/thumb?show_id=${encodeURIComponent(showId || '')}`;
  }
  /** Is card scanning configured? Drives whether the form offers it at all. */
  capScanAvailable() { return api.get(`${PREFIX}/capture/scan-card/available`); }
  /** Read card photo(s) → suggested fields. `fd` is a FormData with 1..n `file`
   *  entries (several = sides of one card). Suggests only; persists nothing. */
  capScanCard(fd) { return api.post(`${PREFIX}/capture/scan-card`, fd); }

  /** Dashboard stat cards + overview data */
  getDashboard(params = {}) {
    return api.get(`${PREFIX}/dashboard`, params);
  }

  // ── Customers ──────────────────────────────────────────────

  /** Paginated customer list */
  getCustomers(params = {}) {
    return api.get(`${PREFIX}/customers`, params);
  }

  /** Full customer detail */
  getCustomerDetail(code, params = {}) {
    return api.get(`${PREFIX}/customers/${encodeURIComponent(code)}`, params);
  }

  /** An account's full order / quote / invoice history, a year at a time (account_documents.py) */
  getCustomerDocuments(code, kind, params = {}) {
    return api.get(`${PREFIX}/customers/${encodeURIComponent(code)}/documents/${kind}`, params);
  }

  /** One document's lines, loaded when it is opened */
  getCustomerDocumentLines(code, kind, number, params = {}) {
    return api.get(`${PREFIX}/customers/${encodeURIComponent(code)}/documents/${kind}/${encodeURIComponent(number)}`, params);
  }

  /** Visited pages with GMC product cards */
  getCustomerVisitPages(code, params = {}) {
    return api.get(`${PREFIX}/customers/${encodeURIComponent(code)}/visit-pages`, params);
  }

  /** Email campaign activity for a customer */
  /** What this account buys, what they last paid, and what they are due. */
  /** A plain URL: the browser's own cookie authenticates it, and it is loaded into a
   *  SANDBOXED iframe — never injected into the page. */
  campaignPreviewUrl(id) { return `/api${PREFIX}/campaigns/${encodeURIComponent(id)}/preview`; }
  /** An account's NPS / Thermometer answers, as isg-nps shows them to this person. */
  getCustomerNps(code, params = {}) { return api.get(`${PREFIX}/customers/${encodeURIComponent(code)}/nps`, params); }
  getCustomerBuys(code, params = {}) { return api.get(`${PREFIX}/customers/${encodeURIComponent(code)}/buys`, params); }
  getCustomerCampaigns(code, params = {}) {
    return api.get(`${PREFIX}/customers/${encodeURIComponent(code)}/campaigns`, params);
  }

  /** Named web journey — identified sessions */
  getCustomerWebJourney(code, params = {}) {
    return api.get(`${PREFIX}/customers/${encodeURIComponent(code)}/web-journey`, params);
  }

  // ── Pipeline ───────────────────────────────────────────────

  /** Pipeline (quotes or orders) */
  getPipeline(params = {}) {
    return api.get(`${PREFIX}/pipeline`, params);
  }

  // ── Revenue ────────────────────────────────────────────────

  /** Revenue aggregation */
  getRevenue(params = {}) {
    return api.get(`${PREFIX}/revenue`, params);
  }

  // ── Opportunities ──────────────────────────────────────────

  /** Opportunity signals */
  getOpportunities(params = {}) {
    return api.get(`${PREFIX}/opportunities`, params);
  }

  /** Rules-based scored opportunities */
  /** Live QX quotes as deals (read-only; 'Track this' creates a real opportunity). */
  getQxPipeline(params = {}) { return api.get(`${PREFIX}/opportunities/qx`, params); }

  getOpportunitiesScored(params = {}) {
    return api.get(`${PREFIX}/opportunities/scored`, params);
  }

  /** Market insights (was My patch) — the market around the person's accounts (isg-marketing's figures, filtered here) */
  getMarketPatch(params = {}) {
    return api.get(`${PREFIX}/market-patch`, params);
  }

  /** Market insights › At a glance — one division's month (the same page as BI Markets and the monthly email) */
  getMarketGlanceDivisions(entity) {
    return api.get(`${PREFIX}/market-glance/divisions`, { entity });
  }

  getMarketGlance(params) {
    return api.get(`${PREFIX}/market-glance`, params);
  }

  /** Company signals — contracts won, news, patents and the registered company behind one account */
  getAccountSignals(code, params = {}) {
    return api.get(`${PREFIX}/customers/${encodeURIComponent(code)}/signals`, params);
  }

  /** The same across every account in reach (`book=mine` narrows), newest first */
  getSignals(params = {}) {
    return api.get(`${PREFIX}/signals`, params);
  }

  /** Companies that do NOT buy from us winning equipment contracts near this entity */
  /** Next moves — the few things to do this week over the accounts in view (Market Intelligence) */
  getNextMoves(params = {}) {
    return api.get(`${PREFIX}/next-moves`, params);
  }

  getNewBusiness(params = {}) {
    return api.get(`${PREFIX}/new-business`, params);
  }

  /** Insights — what our own trade says changed (Opportunities › Insights) */
  getInsights(params = {}) {
    return api.get(`${PREFIX}/insights`, params);
  }

  /** An AI opening line for the top open rows of one kind (gated like every AI call) */
  getInsightLines(params = {}) {
    return api.get(`${PREFIX}/insights/lines`, params);
  }

  /** worked · dismissed (with a reason) · snoozed */
  decideInsight(body) {
    return api.post(`${PREFIX}/insights/decide`, body);
  }

  /** Put a decided row back on the list */
  undoInsight(body) {
    return api.post(`${PREFIX}/insights/undo`, body);
  }

  // ── Web Requests — the enquiries themselves are in the inbox (getInbox, source=web) since
  //    2026-09-28; these two serve an opened web row: its lines, and the admin Verify ──

  /** Verify a web request match */
  verifyWebRequest(id, data = {}) {
    return api.post(`${PREFIX}/web-requests/${id}/verify`, data);
  }

  /** Web request line-level detail (quotes, orders, invoices) */
  getWebRequestDetail(id) {
    return api.get(`${PREFIX}/web-requests/${id}/detail`);
  }

  // ── AI Endpoints ───────────────────────────────────────────

  /** AI capabilities / feature flags */
  /** One suggested next step per top Today item (local model). */
  getTodayActions(params) { return api.get(`${PREFIX}/today/actions`, params); }
  getAiCapabilities() {
    return api.get(`${PREFIX}/ai/capabilities`);
  }

  /** AI customer brief */
  getAiBrief(code, refresh = false) {
    const params = refresh ? { refresh: 'true' } : {};
    return api.get(`${PREFIX}/customers/${encodeURIComponent(code)}/ai-brief`, params);
  }

  /**
   * The brief AS IT IS WRITTEN (SSE). `on(event, data)` is called for
   * meta · delta · done · withheld · error. Returns a promise that settles when the
   * stream ends; call the returned abort to stop it.
   *
   * 🔴 Not `EventSource`: it cannot be aborted cleanly when a rep clicks away
   * mid-brief, and we want the fetch credentials the rest of the client uses.
   */
  streamAiBrief(code, on, { refresh = false, entity } = {}) {
    const ctrl = new AbortController();
    // raw fetch, so build the full path: PREFIX ('/sales') is relative to the
    // ui-kit client's '/api' base, which this call bypasses
    // ⚠️ The ENTITY goes too: without it the server binds the person's home entity and a
    // us or de account 404s (it did, until 2026-09-24).
    const q = new URLSearchParams({ ...(entity ? { entity } : {}), ...(refresh ? { refresh: 'true' } : {}) }).toString();
    const url = `/api${PREFIX}/customers/${encodeURIComponent(code)}/ai-brief/stream${q ? `?${q}` : ''}`;
    const done = (async () => {
      const res = await fetch(withCompany(url), { credentials: 'same-origin', signal: ctrl.signal,
                                     headers: { Accept: 'text/event-stream' } });
      if (!res.ok) throw new Error(res.status === 403 ? 'AI features are not enabled for you'
                                                     : `Could not start the brief (${res.status})`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done: fin } = await reader.read();
        if (fin) break;
        buf += dec.decode(value, { stream: true });
        // ⚠️ SSE frames are separated by a BLANK line and a chunk can split one in
        // half; keep the remainder rather than parsing what has not all arrived.
        const frames = buf.split('\n\n');
        buf = frames.pop();
        for (const f of frames) {
          const ev = (f.match(/^event: (.+)$/m) || [])[1];
          const dataLine = (f.match(/^data: (.+)$/m) || [])[1];
          if (!ev || !dataLine) continue;
          try { on(ev, JSON.parse(dataLine)); } catch { /* a frame we cannot read is not fatal */ }
        }
      }
    })();
    return { done, abort: () => ctrl.abort() };
  }

  /** Ask AI about a customer */
  askAboutCustomer(code, question) {
    return api.post(`${PREFIX}/customers/${encodeURIComponent(code)}/ask`, { question });
  }

  /** AI-enhanced opportunity scoring */
  getAiScoredOpportunities(params = {}) {
    return api.get(`${PREFIX}/opportunities/ai-scored`, params);
  }

  // ── Alerts & Prospects ─────────────────────────────────────

  /** Trend alerts */
  getAlerts(params = {}) {
    return api.get(`${PREFIX}/alerts`, params);
  }

  /** Accounts showing buying signals (website, email, ads) with the evidence.
   *  Replaces getHotProspects, whose /hot-prospects route never existed. */
  getBuyingSignals(params = {}) {
    return api.get(`${PREFIX}/buying-signals`, params);
  }

  // ── Preferences ────────────────────────────────────────────

  /** Get user preferences */
  getPreferences() {
    return api.get(`${PREFIX}/preferences`);
  }

  /** Update user preferences */
  updatePreferences(data) {
    return api.put(`${PREFIX}/preferences`, data);
  }

  // ── Me + Team › Rep codes (Workstream C 0.6) ───────────────
  getMyScope(params = {}) { return api.get(`${PREFIX}/me/scope`, params); }
  getRepCodes(params = {}) { return api.get(`${PREFIX}/team/rep-codes`, params); }
  searchTeamUsers(q) { return api.get(`${PREFIX}/team/users`, { q }); }
  /** Give a show lead to somebody to work, or `null` to take it back. */
  assignLead(id, userId) { return api.post(`${PREFIX}/leads/${encodeURIComponent(id)}/assign`, { user_id: userId }); }
  /** Several at once — ONE email listing them all, a task each. */
  assignLeadsBulk(leadIds, userId) { return api.post(`${PREFIX}/leads/assign`, { lead_ids: leadIds, user_id: userId }); }
  _repCode(entity, kind, code) {
    return `${PREFIX}/team/rep-codes/${encodeURIComponent(entity)}/${encodeURIComponent(kind)}/${encodeURIComponent(code)}`;
  }
  confirmRepLink(entity, kind, code, body) { return api.post(`${this._repCode(entity, kind, code)}/confirm`, body); }
  rejectRepLink(entity, kind, code, body) { return api.post(`${this._repCode(entity, kind, code)}/reject`, body); }
  assignRepCode(entity, kind, code, body) { return api.post(`${this._repCode(entity, kind, code)}/assign`, body); }
  endRepLink(entity, kind, code, body) { return api.post(`${this._repCode(entity, kind, code)}/end`, body); }
  updateRepCode(entity, kind, code, body) { return api.patch(this._repCode(entity, kind, code), body); }
  getRepCodeHistory(entity, kind, code) { return api.get(`${this._repCode(entity, kind, code)}/history`); }
  getBookChecks(params = {}) { return api.get(`${PREFIX}/team/book-checks`, params); }
  /** Today (surface #1) — the ranked queue, built on demand. */
  getToday(params = {}) { return api.get(`${PREFIX}/today`, params); }

  // ── Leads inbox (surface #7): show leads and web enquiries in one list ──
  getInbox(params = {}) { return api.get(`${PREFIX}/inbox`, params); }
  _inboxItem(item) { return `${PREFIX}/inbox/${encodeURIComponent(item.source)}/${encodeURIComponent(item.ref)}`; }
  assignInboxItem(item, userId, entity) {
    return api.post(`${this._inboxItem(item)}/assign?entity=${encodeURIComponent(entity)}`, { user_id: userId });
  }
  /** Drop several as not enquiries — the screened-out pitches and junk (2026-10-26). */
  discardInboxMany(items, note, entity) {
    return api.post(`${PREFIX}/inbox/discard?entity=${encodeURIComponent(entity)}`, { items, note });
  }
  /** Several at once — ONE email for the batch, a task each. */
  assignInboxMany(items, userId, entity) {
    return api.post(`${PREFIX}/inbox/assign?entity=${encodeURIComponent(entity)}`, { items, user_id: userId });
  }
  // ── Quote requests by email / phone / visit, and the response report ──
  // ── Sourcing (any part: what we know, the live market, quote or not) ──
  getSourcingContext(entity, part) { return api.get(`${PREFIX}/sourcing/${encodeURIComponent(entity)}/${encodeURIComponent(part)}`); }
  runSourcing(entity, part, body) { return api.post(`${PREFIX}/sourcing/${encodeURIComponent(entity)}/${encodeURIComponent(part)}/live`, body); }
  getSourcingSearches(params) { return api.get(`${PREFIX}/sourcing-searches`, params); }
  decideSourcing(id, body) { return api.post(`${PREFIX}/sourcing-searches/${encodeURIComponent(id)}/decision`, body); }
  createRequest(body) { return api.post(`${PREFIX}/requests`, body); }
  markRequestQuoted(id, body) { return api.post(`${PREFIX}/requests/${encodeURIComponent(id)}/quoted`, body); }
  deleteRequest(id) { return api.delete(`${PREFIX}/requests/${encodeURIComponent(id)}`); }
  getResponseReport(params) { return api.get(`${PREFIX}/requests/report`, params); }
  acceptInboxItem(item, entity) {
    return api.post(`${this._inboxItem(item)}/accept?entity=${encodeURIComponent(entity)}`, {});
  }
  /** Read an inbound enquiry and draft a quote from it. Nothing is ever sent. */
  draftQuoteFromEnquiry(ref, entity) {
    return api.post(`${PREFIX}/inbox/web/${encodeURIComponent(ref)}/draft-quote?entity=${encodeURIComponent(entity)}`, {});
  }
  setInboxStatus(item, status, note, entity) {
    return api.post(`${this._inboxItem(item)}/status?entity=${encodeURIComponent(entity)}`, { status, note });
  }

  /** Team (surface #8) — the org chart decides whose numbers these are. */
  getTeamAvailable() { return api.get(`${PREFIX}/team/available`); }
  getTeamOverview(params = {}) { return api.get(`${PREFIX}/team/overview`, params); }
  getTeamTasks(params = {}) { return api.get(`${PREFIX}/team/tasks`, params); }
  getTeamCoverage(params = {}) { return api.get(`${PREFIX}/team/coverage`, params); }
  getTeamAnalysis(params = {}) { return api.get(`${PREFIX}/team/analysis`, params); }
  getTeamMorning(params = {}) { return api.get(`${PREFIX}/team/morning`, params); }
  getTeamThroughput(params = {}) { return api.get(`${PREFIX}/team/throughput`, params); }
  getTeamMorningSummary({ entity, date, refresh = false, view = {} }) {
    return api.post(`${PREFIX}/team/morning/summary?${viewQuery(entity, view)}`, { refresh, ...(date ? { date } : {}) });
  }
  /** Platform admins: every manager on the org chart, to pick a team to look at. */
  getTeamManagers(params = {}) { return api.get(`${PREFIX}/team/managers`, params); }
  /** The baseline analysis: four grounded sections, written on demand (~15-20s cold). */
  getTeamBaseline({ entity, period, refresh = false, view = {} }) {
    return api.post(`${PREFIX}/team/analysis/baseline?${viewQuery(entity, view)}`, { period, refresh });
  }
  /** A question about the team, streamed (SSE over POST): meta · delta · done | withheld | error. */
  streamTeamAsk({ entity, period, question, messages, view = {} }, on) {
    return ssePost(`/api${PREFIX}/team/analysis/ask?${viewQuery(entity, view)}`,
      messages ? { period, messages } : { period, question }, on,
      { 403: 'The analyst is not switched on for you' });
  }
  /** A conversation about one account (SSE over POST): meta · delta · done | withheld | error. */
  streamCustomerChat({ entity, code, messages }, on) {
    return ssePost(`/api${PREFIX}/customers/${encodeURIComponent(code)}/chat?${viewQuery(entity)}`,
      { messages }, on, { 403: 'AI features are not enabled for you' });
  }
  getChatTopics() { return api.get(`${PREFIX}/ai/chat-topics`); }

  // ── Meeting plans (app/blueprints/meeting_plans.py) ──
  listMeetingPlans(params = {}) { return api.get(`${PREFIX}/meeting-plans`, params); }
  createMeetingPlan(body) { return api.post(`${PREFIX}/meeting-plans`, body); }
  getMeetingPlan(id) { return api.get(`${PREFIX}/meeting-plans/${encodeURIComponent(id)}`); }
  updateMeetingPlan(id, body) { return api.patch(`${PREFIX}/meeting-plans/${encodeURIComponent(id)}`, body); }
  deleteMeetingPlan(id) { return api.delete(`${PREFIX}/meeting-plans/${encodeURIComponent(id)}`); }
  clearMeetingChat(id) { return api.delete(`${PREFIX}/meeting-plans/${encodeURIComponent(id)}/chat`); }
  searchColleagues(q) { return api.get(`${PREFIX}/meeting-plans/colleagues`, { q }); }
  emailMeetingPlan(id, body) { return api.post(`${PREFIX}/meeting-plans/${encodeURIComponent(id)}/email`, body); }
  meetingPlanPdfUrl(id, download = false) {
    return withCompany(`/api${PREFIX}/meeting-plans/${encodeURIComponent(id)}/pdf${download ? '?download=1' : ''}`);
  }
  /** Write the sections (SSE): meta · section × n · done | error. */
  streamMeetingPlan(id, sections, on) {
    return ssePost(`/api${PREFIX}/meeting-plans/${encodeURIComponent(id)}/generate`,
      sections ? { sections } : {}, on, { 403: 'AI features are not enabled for you, or this is not your plan' });
  }
  /** The back and forth on a plan (SSE), the account chat's events. */
  streamMeetingChat(id, messages, on) {
    return ssePost(`/api${PREFIX}/meeting-plans/${encodeURIComponent(id)}/chat`,
      { messages }, on, { 403: 'AI features are not enabled for you, or this is not your plan' });
  }
  resolveBookCheck(id) { return api.post(`${PREFIX}/team/book-checks/${encodeURIComponent(id)}/resolve`, {}); }

  // ── Your book (0.14) ────────────────────────────────────────
  getMyBook() { return api.get(`${PREFIX}/me/book`); }
  checkMyBook(body) { return api.post(`${PREFIX}/me/book/check`, body); }

  // ── Find (0.14): three sections, fired together ────────────
  findAccounts(q, params = {}) { return api.get(`${PREFIX}/find/accounts`, { q, ...params }); }
  findContacts(q, params = {}) { return api.get(`${PREFIX}/find/contacts`, { q, ...params }); }
  findDocuments(q, params = {}) { return api.get(`${PREFIX}/find/documents`, { q, ...params }); }
  findParts(q, params = {}) { return api.get(`${PREFIX}/find/parts`, { q, ...params }); }
  getPart(entity, part, params = {}) { return api.get(`${PREFIX}/parts/${encodeURIComponent(entity)}/${encodeURIComponent(part)}`, params); }
  /** What else a part is called — vault's register, bound to the entity. Its own
   *  prefix: part numbers carry slashes, so a suffix on the part route would be read
   *  as part of the number. */
  getCrossrefs(entity, part) { return api.get(`${PREFIX}/crossrefs/${encodeURIComponent(entity)}/${encodeURIComponent(part)}`); }
  findXrefs(q, params = {}) { return api.get(`${PREFIX}/find/xrefs`, { q, ...params }); }
  // ── Parts page before a search: product groups + favourites (2026-09-24) ──
  getPartGroups(params = {}) { return api.get(`${PREFIX}/partgroups`, params); }
  getPartGroup(entity, group, by, reorder) {
    return api.get(`${PREFIX}/partgroups/${encodeURIComponent(entity)}/${encodeURIComponent(group || '_')}`,
      reorder ? { by, reorder } : { by });
  }
  getPartFavourites(params = {}) { return api.get(`${PREFIX}/partfav`, params); }
  addPartFavourite(entity, part) { return api.post(`${PREFIX}/partfav`, { entity, part }); }
  removePartFavourite(entity, part) {
    return api.delete(`${PREFIX}/partfav/${encodeURIComponent(entity)}/${encodeURIComponent(part)}`);
  }

  // ── Files on an account, a deal or a note (CRM #7) ──
  /** `{entity, customer?, work_item?, activity?}` → the files, newest first. */
  listAttachments(params) { return api.get(`${PREFIX}/work/attachments`, params); }
  /** One File. ⚠️ FormData, never JSON (ui-kit's api.post would encode the File away). */
  uploadAttachment({ entity, customer_code, work_item_id, activity_id, note, file }) {
    const form = new FormData();
    form.append('entity', entity);
    form.append('customer_code', customer_code);
    if (work_item_id) form.append('work_item_id', work_item_id);
    if (activity_id) form.append('activity_id', activity_id);
    if (note) form.append('note', note);
    form.append('file', file, file.name);
    return api.post(`${PREFIX}/work/attachments`, form);
  }
  deleteAttachment(id) { return api.delete(`${PREFIX}/work/attachments/${encodeURIComponent(id)}`); }

  // ── Quotes built through isg-pricing's engine (0.8) — the record lives in pricing ──
  /** Read a customer's document (File) or pasted text into quote lines.
   *  ⚠️ A File goes as FormData — ui-kit's api.post would JSON-encode it away. */
  parseQuoteDocument({ entity, customer_code, file, raw }) {
    if (!file) return api.post(`${PREFIX}/quotes/parse`, { entity, customer_code, raw });
    const form = new FormData();
    form.append('entity', entity);
    if (customer_code) form.append('customer_code', customer_code);
    form.append('file', file, file.name);
    return api.post(`${PREFIX}/quotes/parse`, form);
  }
  /** Who buys it (in reach), lots / shelf life / origin, suppliers, purchasing's notices. */
  getPartInfo(entity, part) {
    return api.get(`${PREFIX}/partinfo/${encodeURIComponent(entity)}/${encodeURIComponent(part)}`);
  }
  /** Vault's technical record: specs, datasheets, images, lifecycle, documents. */
  getPartSpec(entity, part) {
    return api.get(`${PREFIX}/partspec/${encodeURIComponent(entity)}/${encodeURIComponent(part)}`);
  }
  /** Competitor price, stock and lead time (pricing's readings) + our demand + HOT / HARD TO FIND. */
  getPartMarket(entity, part, customer) {
    return api.get(`${PREFIX}/partmarket/${encodeURIComponent(entity)}/${encodeURIComponent(part)}`,
      customer ? { customer } : {});
  }
  /** Customers who buy this also buy… (invoices; `customer` marks what they do not buy yet). */
  getPartRelated(entity, part, customer) {
    return api.get(`${PREFIX}/partrelated/${encodeURIComponent(entity)}/${encodeURIComponent(part)}`,
      customer ? { customer } : {});
  }
  pollQuoteParse(jobId, entity) { return api.get(`${PREFIX}/quotes/parse/${encodeURIComponent(jobId)}`, { entity }); }
  /** Quotes for an account that lapsed without being won — the win-back list. */
  getWinbackQuotes(entity, customer, params = {}) {
    return api.get(`${PREFIX}/quotes/winback`, { entity, customer, ...params });
  }
  getWinbackQuote(quoteNo, entity, customer) {
    return api.get(`${PREFIX}/quotes/winback/${encodeURIComponent(quoteNo)}`, { entity, customer });
  }
  /** ⚠️ `context` picks a different LIST, not a filter: a win-back strategy prices off
   *  the quote being regenerated, a standard one knows nothing about one. */
  getQuoteStrategies(entity, customer, context) {
    return api.get(`${PREFIX}/quotes/strategies`, { entity, customer, context: context || 'standard' });
  }
  buildQuote(body) { return api.post(`${PREFIX}/quotes/build`, body); }
  saveQuote(body) { return api.post(`${PREFIX}/quotes`, body); }
  getAccountQuotes(entity, customer) { return api.get(`${PREFIX}/quotes`, { entity, customer }); }
  getQuote(id) { return api.get(`${PREFIX}/quotes/${encodeURIComponent(id)}`); }
  updateQuote(id, body) { return api.patch(`${PREFIX}/quotes/${encodeURIComponent(id)}`, body); }
  updateQuoteLine(id, lineId, body) { return api.patch(`${PREFIX}/quotes/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}`, body); }
  addQuoteLine(id, body) { return api.post(`${PREFIX}/quotes/${encodeURIComponent(id)}/lines`, body); }
  deleteQuoteLine(id, lineId) { return api.delete(`${PREFIX}/quotes/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}`); }
  regenerateQuote(id, clearOverrides) { return api.post(`${PREFIX}/quotes/${encodeURIComponent(id)}/regenerate`, { clear_overrides: !!clearOverrides }); }
  /** A plain link: the browser's own cookie authenticates a GET, so it opens in a tab. */
  quotePdfUrl(id) { return `/api${PREFIX}/quotes/${encodeURIComponent(id)}/pdf`; }
  /** A QX import CSV for the quote — `kind` 'quote' | 'order' (an order needs the PO number). */
  quoteQxUrl(id, kind, po) {
    const qs = new URLSearchParams({ kind });
    if (po) qs.set('po', po);
    return `/api${PREFIX}/quotes/${encodeURIComponent(id)}/qx-export?${qs}`;
  }
  /** Who a quote would go to, who is blocked, and which send mode is on. */
  getQuoteRecipients(id) { return api.get(`${PREFIX}/quotes/${encodeURIComponent(id)}/recipients`); }
  sendQuote(id, body) { return api.post(`${PREFIX}/quotes/${encodeURIComponent(id)}/send`, body); }
  // ── below-floor approvals (2026-09-30) — app/services/floor.py ──
  getQuoteFloor(id) { return api.get(`${PREFIX}/quotes/${encodeURIComponent(id)}/floor`); }
  justifyQuoteFloor(id, body) { return api.post(`${PREFIX}/quotes/${encodeURIComponent(id)}/floor`, body); }
  getApprovals(view = 'to_decide', period = 'all') { return api.get(`${PREFIX}/approvals`, { view, period }); }
  decideApproval(id, body) { return api.post(`${PREFIX}/approvals/${encodeURIComponent(id)}/decide`, body); }
  getFloorPolicies(entity) { return api.get(`${PREFIX}/floor-policies`, entity ? { entity } : {}); }
  putFloorPolicy(body) { return api.put(`${PREFIX}/floor-policies`, body); }

  // ── Prospects (CRM #6, 2026-09-28 — Callum: "both, one list") ──
  //    QX's own # accounts and companies QX does not know yet, in ONE list.
  /** One list, two kinds (QX `#` accounts and ours). The server's row is translated to the
   *  names pages/prospects.js reads, in ONE place: prospect_id → id, qx_owners → qx_people,
   *  last_touch_at → last_touch, live_quotes (count) + live_quote_value → {count, value, currency}. */
  async getProspects(params = {}) {
    const res = await api.get(`${PREFIX}/prospects`, params);
    const d = (res && res.data) || {};
    d.items = (d.items || []).map(r => ({
      ...r,
      id: r.prospect_id ?? r.id ?? null,
      qx_people: r.qx_owners || r.qx_people || null,
      last_touch: r.last_touch_at || r.last_touch || null,
      live_quotes: (r.live_quotes && typeof r.live_quotes === 'object') ? r.live_quotes
        : { count: r.live_quotes || 0, value: r.live_quote_value || 0, currency: r.currency },
    }));
    return res;
  }
  createProspect(body) { return api.post(`${PREFIX}/prospects`, body); }
  /** Make a prospect from a lead / web enquiry / request that has no account. */
  prospectFrom(source, ref, body = {}) {
    return api.post(`${PREFIX}/prospects/from/${encodeURIComponent(source)}/${encodeURIComponent(ref)}`, body);
  }
  getProspect(id) { return api.get(`${PREFIX}/prospects/${encodeURIComponent(id)}`); }
  updateProspect(id, body) { return api.patch(`${PREFIX}/prospects/${encodeURIComponent(id)}`, body); }
  /** A PERSON says which QX account (# or real) this prospect now is — never matched automatically. */
  linkProspect(id, code) { return api.post(`${PREFIX}/prospects/${encodeURIComponent(id)}/link`, { code }); }
  getProspectLinkSuggestions(id) { return api.get(`${PREFIX}/prospects/${encodeURIComponent(id)}/link-suggestions`); }

  // ── What reps record (0.7) ──────────────────────────────────
  logActivity(body) { return api.post(`${PREFIX}/work/activities`, body); }
  getActivities(params = {}) { return api.get(`${PREFIX}/work/activities`, params); }
  // @mentions (CRM #7)
  getMentions(params = {}) { return api.get(`${PREFIX}/work/mentions`, params); }
  markMentionSeen(id) { return api.post(`${PREFIX}/work/mentions/${encodeURIComponent(id)}/seen`, {}); }
  markAllMentionsSeen() { return api.post(`${PREFIX}/work/mentions/seen-all`, {}); }
  getMentionable(params = {}) { return api.get(`${PREFIX}/work/mentionable`, params); }
  createTask(body) { return api.post(`${PREFIX}/work/tasks`, body); }
  getTasks(params = {}) { return api.get(`${PREFIX}/work/tasks`, params); }
  updateTask(id, body) { return api.patch(`${PREFIX}/work/tasks/${encodeURIComponent(id)}`, body); }

  // ── What a person is WORKING: two kinds, ONE record (2026-09-17).
  //    `opportunity` = a single deal — the QX sense of the word; the scored list on
  //    /opportunities SUGGESTS them. `project` = a programme or bid that HOLDS
  //    several. The record itself is addressed by id, whichever kind it is.
  createWorkItem(kind, body) { return api.post(`${PREFIX}/work/${kind === 'project' ? 'projects' : 'opportunities'}`, body); }
  getWorkItems(kind, params = {}) { return api.get(`${PREFIX}/work/${kind === 'project' ? 'projects' : 'opportunities'}`, params); }
  getPriceBooks(entity, customer) { return api.get(`${PREFIX}/quotes/price-books`, { entity, customer }); }
  getPriceBookLines(entity, customer, book) { return api.get(`${PREFIX}/quotes/price-books`, { entity, customer, book }); }
  getAccountPeople(entity, code) { return api.get(`${PREFIX}/customers/${encodeURIComponent(code)}/people`, { entity }); }
  saveContact(body) { return api.post(`${PREFIX}/contacts`, body); }
  updateContact(id, body) { return api.patch(`${PREFIX}/contacts/${encodeURIComponent(id)}`, body); }
  deleteContact(id) { return api.delete(`${PREFIX}/contacts/${encodeURIComponent(id)}`); }
  getDealPeople(id) { return api.get(`${PREFIX}/work/items/${encodeURIComponent(id)}/people`); }
  addDealPerson(id, key, role) { return api.post(`${PREFIX}/work/items/${encodeURIComponent(id)}/people`, { key, role }); }
  removeDealPerson(id, contactId) { return api.delete(`${PREFIX}/work/items/${encodeURIComponent(id)}/people/${encodeURIComponent(contactId)}`); }
  getAccountTimeline(code, params) { return api.get(`${PREFIX}/work/accounts/${encodeURIComponent(code)}/timeline`, params); }
  getWorkItemTimeline(id) { return api.get(`${PREFIX}/work/items/${encodeURIComponent(id)}/timeline`); }
  getAccountRequests(entity, customer) { return api.get(`${PREFIX}/requests`, { entity, customer }); }
  getForecast(kind, params = {}) { return api.get(`${PREFIX}/work/${kind === 'project' ? 'projects' : 'opportunities'}/forecast`, params); }
  getWorkItem(id) { return api.get(`${PREFIX}/work/items/${encodeURIComponent(id)}`); }
  getWorkItemParts(id) { return api.get(`${PREFIX}/work/items/${encodeURIComponent(id)}/parts`); }
  // the parts read from an RFQ (Outlook add-in → Sales reads them in the background)
  getRequestLines(id) { return api.get(`${PREFIX}/requests/${encodeURIComponent(id)}/lines`); }
  /** The emails on an RFQ — preview, attachments, the thread (rfq_mail.py). */
  getRequestEmails(id) { return api.get(`${PREFIX}/requests/${encodeURIComponent(id)}/emails`); }
  /** Feedback → the intranet's Feedback tracker, as the person (feedback.py). */
  sendFeedback(body) { return api.post(`${PREFIX}/feedback`, body); }
  /** Delete a never-sent quote (soft, in pricing); withdraw any quote. */
  // ui-kit's api.delete() sends no body — the reason travels via request()
  deleteQuote(id, body = {}) { return api.request(`${PREFIX}/quotes/${encodeURIComponent(id)}`, { method: 'DELETE', body: JSON.stringify(body) }); }
  withdrawQuote(id, body = {}) { return api.post(`${PREFIX}/quotes/${encodeURIComponent(id)}/withdraw`, body); }
  putRequestLines(id, lines) { return api.put(`${PREFIX}/requests/${encodeURIComponent(id)}/lines`, { lines }); }
  trackRequest(id, kind) { return api.post(`${PREFIX}/requests/${encodeURIComponent(id)}/track`, { kind }); }
  readRequestAgain(id) { return api.post(`${PREFIX}/requests/${encodeURIComponent(id)}/read`, {}); }
  getSupplierSuggestions(id) { return api.get(`${PREFIX}/work/items/${encodeURIComponent(id)}/supplier-suggestions`); }
  searchSuppliers(entity, q) { return api.get(`${PREFIX}/work/suppliers`, { entity, q }); }
  updateWorkItem(id, body) { return api.patch(`${PREFIX}/work/items/${encodeURIComponent(id)}`, body); }
  deleteWorkItem(id) { return api.delete(`${PREFIX}/work/items/${encodeURIComponent(id)}`); }
}

/** The CSRF header ui-kit's client sends with cookie auth — needed by a raw fetch POST. */
/**
 * POST a JSON body and read the answer as SSE — the chat windows' transport.
 * `on(event, data)` per frame. Returns {done, abort}. A frame split across chunks is
 * kept until its blank line arrives.
 */
function ssePost(url, body, on, errors = {}) {
  const ctrl = new AbortController();
  const done = (async () => {
    const res = await fetch(withCompany(url), { method: 'POST', credentials: 'same-origin', signal: ctrl.signal,
      headers: { Accept: 'text/event-stream', 'Content-Type': 'application/json', ...csrfHeader() },
      body: JSON.stringify(body) });
    if (!res.ok) {
      throw new Error(errors[res.status] || (res.status === 429 ? 'Too many questions in a minute — try again shortly'
        : res.status === 404 ? 'Not found, or outside your access' : `Could not ask (${res.status})`));
    }
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = '';
    for (;;) {
      const { value, done: fin } = await reader.read();
      if (fin) break;
      buf += dec.decode(value, { stream: true });
      const frames = buf.split('\n\n');
      buf = frames.pop();
      for (const f of frames) {
        const ev = (f.match(/^event: (.+)$/m) || [])[1];
        const dataLine = (f.match(/^data: (.+)$/m) || [])[1];
        if (!ev || !dataLine) continue;
        try { on(ev, JSON.parse(dataLine)); } catch { /* a frame we cannot read is not fatal */ }
      }
    }
  })();
  return { done, abort: () => ctrl.abort() };
}

/** `entity=…&manager=…&division=…` for a POST whose view rides on the query string. */
function viewQuery(entity, view = {}) {
  const q = new URLSearchParams({ entity });
  for (const [k, v] of Object.entries(view || {})) if (v) q.set(k, v);
  return q.toString();
}

function csrfHeader() {
  const m = document.cookie.match(/(?:^|; )csrf_access_token=([^;]*)/);
  return m ? { 'X-CSRF-TOKEN': decodeURIComponent(m[1]) } : {};
}

export const salesApi = new SalesAPI();
